/**
 * Serialization and deserialization utilities for schedule parameter values.
 *
 * - serializeParams: converts ScheduleParameterValues to the API payload format.
 * - deserializeParams: converts raw API response back to ScheduleParameterValues,
 *   handling legacy plain values by normalizing them to structured entries.
 */
import type { ScheduleParameterEntry, ScheduleParameterValues } from "@/types";

/**
 * Detects whether a raw parameter entry is in the structured format (has a "mode" key)
 * vs. legacy plain value format.
 */
function isStructuredParam(entry: unknown): entry is ScheduleParameterEntry {
  return typeof entry === "object" && entry !== null && "mode" in entry;
}

/**
 * Serialize ScheduleParameterValues to the API payload format.
 *
 * Each entry is already in the structured format ({ value, mode, offset? }),
 * so this is essentially a pass-through that strips any unexpected keys
 * and produces a clean Record<string, unknown> for the request body.
 */
export function serializeParams(
  values: ScheduleParameterValues,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, entry] of Object.entries(values)) {
    const serialized: Record<string, unknown> = {
      value: entry.value,
      mode: entry.mode,
    };
    if (entry.offset !== undefined) {
      serialized.offset = entry.offset;
    }
    result[key] = serialized;
  }

  return result;
}

/**
 * Deserialize raw API response parameters back to ScheduleParameterValues.
 *
 * Handles two cases:
 * 1. Structured entries (dict with "mode" key): pass through as ScheduleParameterEntry.
 * 2. Legacy entries (plain values without "mode"): wrap as { value: rawValue, mode: "static" }.
 */
export function deserializeParams(
  raw: Record<string, unknown>,
): ScheduleParameterValues {
  const result: ScheduleParameterValues = {};

  for (const [key, entry] of Object.entries(raw)) {
    if (isStructuredParam(entry)) {
      // Structured format — extract the known fields
      result[key] = {
        value: entry.value,
        mode: entry.mode,
        ...(entry.offset !== undefined ? { offset: entry.offset } : {}),
      };
    } else {
      // Legacy plain value — normalize to structured with mode: "static"
      result[key] = {
        value: entry,
        mode: "static",
      };
    }
  }

  return result;
}
